import { z } from 'zod';

/**
 * Zod schemas for the 8 promotion action types.
 *
 * These describe the FORM (UI) shape, keyed on a string `type` discriminator.
 * The wire representation is `CartActionToken { ActionType: string, Payload: any }`
 * (the backend stores action config as free-form JSON — see 08-backend.md §6 and
 * Core/client/api/models/cart-action-token.ts). The form↔Payload mapping lives in
 * the mutation hook layer; the exact Payload field names must be reconciled
 * against the existing admin editor / C# IPromotionActionConfig implementations
 * when wiring real saves (Phase F/I).
 *
 * The non-negotiable backend constraint (08-backend.md §4) that lives in the
 * action layer is encoded here:
 *  - #4 per-product-discount: maxQty is a hard GATING cap (cart qty > maxQty ⇒
 *    the whole policy is gated off); the schema enforces maxQty ≥ minQty.
 */

export const ACTION_TYPES = [
	'percentage-discount',
	'value-discount',
	'fixed-price',
	'bogo',
	'gift-product',
	'per-product-discount',
	'category-discount',
	'shipping-discount'
] as const;

export type ActionType = (typeof ACTION_TYPES)[number];

/** Tier-table mode: fixed (single tier), or scaled by quantity / cart amount. */
export const tierModeSchema = z.enum(['fixed', 'byQuantity', 'byAmount']);
export type TierMode = z.infer<typeof tierModeSchema>;

/** A single tier row. `threshold` is qty or € depending on tierMode; `value` is %/€/price. */
export const tierSchema = z.object({
	id: z.string(),
	threshold: z.number().nonnegative(),
	value: z.number().nonnegative()
});
export type Tier = z.infer<typeof tierSchema>;

/** Behavior when the matched product already carries another discount. */
export const applyOnDiscountedSchema = z.enum(['none', 'max', 'additive', 'onFinal']);

const baseAction = { combineWithOthers: z.boolean().default(false) };

/** How a per-row discount is read: percentage (≤ 100) or fixed € amount per unit (wire `DiscountMode` 0/1). */
export const discountModeSchema = z.enum(['percentage', 'amount']);
export type DiscountMode = z.infer<typeof discountModeSchema>;

/** How BOGO reach (X) items are charged (wire `BogoReachPricingMode` 0/1). */
export const bogoReachPricingModeSchema = z.enum(['afterDiscount', 'beforeDiscount']);
export type BogoReachPricingMode = z.infer<typeof bogoReachPricingModeSchema>;

/** Percentage discounts are capped at 100; amount discounts only need to be non-negative. */
const discountWithinMode = (discount: number | null, mode: DiscountMode) => discount == null || mode === 'amount' || discount <= 100;

export const percentageDiscountSchema = z.object({
	type: z.literal('percentage-discount'),
	...baseAction,
	tierMode: tierModeSchema.default('fixed'),
	tiers: z.array(tierSchema).min(1),
	applyOnDiscountedProducts: applyOnDiscountedSchema.default('none')
});

export const valueDiscountSchema = z.object({
	type: z.literal('value-discount'),
	...baseAction,
	applicationMode: z.enum(['once', 'perItem']).default('once'),
	tierMode: tierModeSchema.default('fixed'),
	tiers: z.array(tierSchema).min(1)
});

export const fixedPriceSchema = z.object({
	type: z.literal('fixed-price'),
	...baseAction,
	tierMode: tierModeSchema.default('fixed'),
	tiers: z.array(tierSchema).min(1)
});

/**
 * BW-8222 offer-target row: `id` is a product id (offerProducts) or category id (offerCategories).
 * A null `discount` falls back to the action's `z` percentage; `maxQty` applies to product rows only.
 */
export const bogoOfferTargetSchema = z
	.object({
		id: z.number().int().positive(),
		discount: z.number().positive().nullable(),
		discountMode: discountModeSchema.default('percentage'),
		maxQty: z.number().int().positive().nullable()
	})
	.refine(r => discountWithinMode(r.discount, r.discountMode), { message: 'percentageAbove100', path: ['discount'] });
export type BogoOfferTarget = z.infer<typeof bogoOfferTargetSchema>;

const hasOwnDiscount = (r: BogoOfferTarget) => r.discount != null && r.discount > 0;

/** True when the BOGO percentage may be left empty: offer targets exist and every row carries its own discount. */
export function bogoPercentageOptional(offerProducts: BogoOfferTarget[], offerCategories: BogoOfferTarget[]): boolean {
	const rows = [...offerProducts, ...offerCategories];
	return rows.length > 0 && rows.every(hasOwnDiscount);
}

export const bogoSchema = z
	.object({
		type: z.literal('bogo'),
		...baseAction,
		x: z.number().int().positive(),
		y: z.number().int().positive(),
		/** Discount % of classic X+Y and the default for offer rows without their own discount; null only when every row has one. */
		z: z.number().min(0).max(100).nullable(),
		sameProductOnly: z.boolean().default(false),
		/** Wire `CombinedDiscountsMethod` (set by the legacy editor); not edited here, carried through untouched. */
		combinedDiscountsMethod: z.number().int().optional(),
		reachPricingMode: bogoReachPricingModeSchema.default('afterDiscount'),
		offerProducts: z.array(bogoOfferTargetSchema).default([]),
		offerCategories: z.array(bogoOfferTargetSchema).default([])
	})
	.refine(a => a.z != null || bogoPercentageOptional(a.offerProducts, a.offerCategories), {
		message: 'bogoPercentageRequired',
		path: ['z']
	});

/** One localized prompt row (locale ISO-2 → text). */
export const giftPromptSchema = z.object({ locale: z.string(), text: z.string() });

export const giftProductSchema = z.object({
	type: z.literal('gift-product'),
	...baseAction,
	maxSelections: z.number().int().positive(),
	productIds: z.array(z.number()).min(1),
	prompts: z.array(giftPromptSchema)
});

/**
 * Per-product discount row. Constraint #4: maxQty is a GATING cap, not a soft
 * limit — enforce maxQty ≥ minQty so the row is satisfiable.
 */
export const perProductRowSchema = z
	.object({
		productId: z.number(),
		minQty: z.number().int().positive(),
		/** Must be > 0: the server drops rows with Discount <= 0. */
		discount: z.number().positive(),
		discountMode: discountModeSchema.default('percentage'),
		/** null = unlimited (legacy `MaxQuantity: null`). */
		maxQty: z.number().int().positive().nullable()
	})
	.refine(r => r.maxQty == null || r.maxQty >= r.minQty, {
		message: 'maxQtyBelowMinQty',
		path: ['maxQty']
	})
	.refine(r => discountWithinMode(r.discount, r.discountMode), { message: 'percentageAbove100', path: ['discount'] });

export const perProductDiscountSchema = z.object({
	type: z.literal('per-product-discount'),
	...baseAction,
	rows: z.array(perProductRowSchema).min(1)
});

/**
 * Category discount row (wire `CategoryDiscountCartAction.Item`): every cart product of `categoryId`
 * is offered `discount` (% or € per `discountMode`); `maxQty` empty = no limit.
 */
export const categoryDiscountRowSchema = z
	.object({
		categoryId: z.number().int().positive(),
		minQty: z.number().int().positive(),
		discount: z.number().positive(),
		discountMode: discountModeSchema.default('percentage'),
		maxQty: z.number().int().positive().nullable()
	})
	.refine(r => discountWithinMode(r.discount, r.discountMode), { message: 'percentageAbove100', path: ['discount'] });

export const categoryDiscountSchema = z.object({
	type: z.literal('category-discount'),
	...baseAction,
	maxChoices: z.number().int().positive().nullable(),
	rows: z.array(categoryDiscountRowSchema).min(1),
	/** Wire `Messages` (locale → OfferProductCartActionMessages); not edited here, carried through untouched. */
	messages: z.record(z.string(), z.unknown()).default({})
});

export const shippingRuleSchema = z.object({
	id: z.string(),
	shippingMethodId: z.number().nullable(),
	discountType: z.enum(['percentage', 'fixed', 'free']),
	amount: z.number().nonnegative(),
	stopProcessing: z.boolean().default(false),
	additive: z.boolean().default(false)
});

export const shippingDiscountSchema = z.object({
	type: z.literal('shipping-discount'),
	...baseAction,
	rules: z.array(shippingRuleSchema).min(1)
});

export const actionSchema = z.discriminatedUnion('type', [
	percentageDiscountSchema,
	valueDiscountSchema,
	fixedPriceSchema,
	bogoSchema,
	giftProductSchema,
	perProductDiscountSchema,
	categoryDiscountSchema,
	shippingDiscountSchema
]);

export type PromotionAction = z.infer<typeof actionSchema>;
